import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { PulpPaperRoute } from '../../types';

/** 文化纸机 P&ID 详细图：单流浆箱 → 网部 → 压榨 → 两段干燥 → 压光 → 卷取 */
const media = {
  pulp: { label: '浆料', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  paper: { label: '纸页', light: '#6d4c41', dark: '#a1887f', width: 2.6 },
  steam: { label: '干燥蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  water: { label: '白水 / 冷凝水', light: '#1565c0', dark: '#64b5f6', width: 2 },
};

const devices = [
  { id: 'sb', kind: 'hDrum' as const, x: 170, y: 189, tag: 'SB-101', name: '流浆箱' },
  { id: 'wb', kind: 'washer' as const, x: 380, y: 188, tag: 'WB-101', name: '网部' },
  { id: 'pr', kind: 'washer' as const, x: 530, y: 188, tag: 'PR-101', name: '压榨部' },
  { id: 'dr1', kind: 'exchanger' as const, x: 660, y: 188, tag: 'DR-101', name: '前烘' },
  { id: 'sg', kind: 'washer' as const, x: 810, y: 181, tag: 'SG-101', name: '施胶机', note: '表面施胶' },
  { id: 'dr2', kind: 'exchanger' as const, x: 960, y: 188, tag: 'DR-102', name: '后烘' },
  { id: 'cl', kind: 'washer' as const, x: 1110, y: 188, tag: 'CL-101', name: '压光机' },
  { id: 'rd', kind: 'hDrum' as const, x: 1260, y: 189, tag: 'RD-101', name: '卷取' },
];

const ports: PidPort[] = [
  { id: 'p-pulp', label: '文化纸浆', medium: 'pulp', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-stm', label: '干燥蒸汽', medium: 'steam', x: 630, y: 40, direction: 'in' as const },
  { id: 'p-paper', label: '成品纸卷', medium: 'paper', x: 1430, y: 224, direction: 'out' as const },
  { id: 'p-white', label: '白水回收', medium: 'water', x: 290, y: 611, direction: 'out' as const },
  { id: 'p-cond', label: '凝结水', medium: 'water', x: 1030, y: 470, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 'k01', from: 'p-pulp', fromSide: 'right', to: 'sb', toSide: 'left', medium: 'pulp' },
  { id: 'k02', from: 'sb', fromSide: 'right', to: 'wb', toSide: 'left', medium: 'pulp' },
  { id: 'k03', from: 'wb', fromSide: 'right', to: 'pr', toSide: 'left', medium: 'paper' },
  { id: 'k04', from: 'pr', fromSide: 'right', to: 'dr1', toSide: 'left', medium: 'paper' },
  { id: 'k05', from: 'dr1', fromSide: 'right', to: 'sg', toSide: 'left', medium: 'paper' },
  { id: 'k06', from: 'sg', fromSide: 'right', to: 'dr2', toSide: 'left', medium: 'paper' },
  { id: 'k07', from: 'dr2', fromSide: 'right', to: 'cl', toSide: 'left', medium: 'paper' },
  { id: 'k08', from: 'cl', fromSide: 'right', to: 'rd', toSide: 'left', medium: 'paper' },
  { id: 'k09', from: 'rd', fromSide: 'right', to: 'p-paper', toSide: 'left', medium: 'paper' },
  { id: 'k10', from: 'p-stm', fromSide: 'left', to: 'dr1', toSide: 'top', medium: 'steam' },
  { id: 'k11', from: 'p-stm', fromSide: 'left', to: 'dr2', toSide: 'top', medium: 'steam' },
  { id: 'k12', from: 'dr1', fromSide: 'bottom', to: 'p-cond', toSide: 'left', medium: 'water' },
  { id: 'k13', from: 'dr2', fromSide: 'bottom', to: 'p-cond', toSide: 'left', medium: 'water' },
  { id: 'k14', from: 'wb', fromSide: 'bottom', to: 'p-white', toSide: 'left', medium: 'water' },
];

const CulturalPaperDiagram: React.FC<{ route: PulpPaperRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="文化纸机详细流程"
    subtitle="流浆箱 → 网部成型 → 压榨脱水 → 前烘干燥 → 表面施胶 → 后烘干燥（蒸汽加热、凝结水回收）→ 压光 → 卷取；网部白水回收（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={route.products}
  />
);

export default CulturalPaperDiagram;
